import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';

interface ModelPricing {
  id?: number;
  model_name: string;
  service_name: string;
  provider: string;
  input_price_per_1k_tokens: number;
  output_price_per_1k_tokens: number;
  minimum_cost_per_call: number;
  version?: string;
  valid_from?: string;
  valid_to?: string;
  is_active?: boolean;
}

interface PricingEditForm {
  model_name: string;
  service_name: string;
  provider: string;
  input_price_per_1k_tokens: string;
  output_price_per_1k_tokens: string;
  minimum_cost_per_call: string;
}

const PricingManagementPage: React.FC = () => {
  const [models, setModels] = useState<ModelPricing[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingModel, setEditingModel] = useState<ModelPricing | null>(null);
  const [formData, setFormData] = useState<PricingEditForm>({
    model_name: '',
    service_name: '',
    provider: '',
    input_price_per_1k_tokens: '0',
    output_price_per_1k_tokens: '0',
    minimum_cost_per_call: '0',
  });

  const getAuthToken = () => {
    return localStorage.getItem('accessToken') || localStorage.getItem('authToken') || localStorage.getItem('token') || '';
  };

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/billing/pricing/models', {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch pricing');
      }

      const data = await response.json();
      // Convert string values to numbers
      const models = (data.models || []).map((m: any) => ({
        ...m,
        input_price_per_1k_tokens: Number(m.input_price_per_1k_tokens) || 0,
        output_price_per_1k_tokens: Number(m.output_price_per_1k_tokens) || 0,
        minimum_cost_per_call: Number(m.minimum_cost_per_call) || 0,
      }));
      setModels(models);
    } catch (err: any) {
      setError(err.message);
      showMessage('error', `Failed to fetch pricing: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (model?: ModelPricing) => {
    if (model) {
      setEditingModel(model);
      setFormData({
        model_name: model.model_name,
        service_name: model.service_name,
        provider: model.provider,
        input_price_per_1k_tokens: String(model.input_price_per_1k_tokens),
        output_price_per_1k_tokens: String(model.output_price_per_1k_tokens),
        minimum_cost_per_call: String(model.minimum_cost_per_call),
      });
    } else {
      setEditingModel(null);
      setFormData({
        model_name: '',
        service_name: 'mcp',
        provider: 'openai',
        input_price_per_1k_tokens: '0',
        output_price_per_1k_tokens: '0',
        minimum_cost_per_call: '0',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingModel(null);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSavePricing = async () => {
    try {
      if (!formData.model_name || !formData.service_name) {
        showMessage('error', 'Model name and service are required');
        return;
      }

      setLoading(true);

      const payload = {
        model_name: formData.model_name,
        service_name: formData.service_name,
        provider: formData.provider,
        input_price_per_1k_tokens: parseFloat(formData.input_price_per_1k_tokens),
        output_price_per_1k_tokens: parseFloat(formData.output_price_per_1k_tokens),
        minimum_cost_per_call: parseFloat(formData.minimum_cost_per_call),
      };

      const response = await fetch('/api/billing/pricing/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Failed to save pricing (${response.status})`,
        );
      }

      showMessage('success', '✅ Pricing updated successfully');
      handleCloseDialog();
      await fetchPricing();
    } catch (err: any) {
      showMessage('error', `Failed to save pricing: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (model: ModelPricing) => {
    const now = new Date();
    const validFrom = model.valid_from ? new Date(model.valid_from) : null;
    const validTo = model.valid_to ? new Date(model.valid_to) : null;

    if (model.is_active === false) {
      return <Typography variant="caption" sx={{ color: '#f57c00' }}>⏱️ Expired</Typography>;
    }
    if (!model.is_active) {
      return <Typography variant="caption" sx={{ color: '#9e9e9e' }}>❌ Inactive</Typography>;
    }
    return <Typography variant="caption" sx={{ color: '#4caf50' }}>✅ Active</Typography>;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        💲 Pricing Management
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        When you update pricing, the old pricing is automatically expired (valid_to = now) and new pricing becomes active.
        This ensures accurate cost tracking for each billing period.
      </Alert>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {loading && <CircularProgress />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Model Pricing Configurations"
          subheader={`${models.filter((m) => m.is_active !== false).length} active models`}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              disabled={loading}
            >
              Add New Model
            </Button>
          }
        />
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Model</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell align="right">Input (/1K tokens)</TableCell>
                  <TableCell align="right">Output (/1K tokens)</TableCell>
                  <TableCell align="right">Min Cost</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Version</TableCell>
                  <TableCell align="center">Valid From</TableCell>
                  <TableCell align="center">Valid To</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {models.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No pricing configurations found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  models.map((model, idx) => (
                    <TableRow key={idx} sx={{ backgroundColor: model.is_active === false ? '#fafafa' : 'white' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>{model.model_name}</TableCell>
                      <TableCell>{model.service_name}</TableCell>
                      <TableCell>{model.provider}</TableCell>
                      <TableCell align="right">${Number(model.input_price_per_1k_tokens).toFixed(6)}</TableCell>
                      <TableCell align="right">${Number(model.output_price_per_1k_tokens).toFixed(6)}</TableCell>
                      <TableCell align="right">${Number(model.minimum_cost_per_call).toFixed(6)}</TableCell>
                      <TableCell align="center">{getStatusBadge(model)}</TableCell>
                      <TableCell align="center">{model.version || 'v1'}</TableCell>
                      <TableCell align="center">
                        <Typography variant="caption">
                          {model.valid_from
                            ? new Date(model.valid_from).toLocaleDateString()
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="caption"
                          sx={{ color: model.valid_to ? '#f57c00' : '#4caf50' }}
                        >
                          {model.valid_to
                            ? new Date(model.valid_to).toLocaleDateString()
                            : 'Active'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {model.is_active !== false && (
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleOpenDialog(model)}
                            disabled={loading}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Edit/Add Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingModel ? 'Update Model Pricing' : 'Add New Model Pricing'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            fullWidth
            label="Model Name"
            name="model_name"
            value={formData.model_name}
            onChange={handleFormChange}
            margin="normal"
            disabled={!!editingModel}
            placeholder="e.g., gpt-4o, text-embedding-3-small"
          />

          <FormControl fullWidth margin="normal">
            <InputLabel>Service</InputLabel>
            <Select
              name="service_name"
              value={formData.service_name}
              onChange={handleFormChange}
            >
              <MenuItem value="mcp">MCP</MenuItem>
              <MenuItem value="bot">BOT</MenuItem>
              <MenuItem value="api">API</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Provider</InputLabel>
            <Select
              name="provider"
              value={formData.provider}
              onChange={handleFormChange}
            >
              <MenuItem value="openai">OpenAI</MenuItem>
              <MenuItem value="azure">Azure OpenAI</MenuItem>
              <MenuItem value="anthropic">Anthropic</MenuItem>
              <MenuItem value="google">Google</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Input Price per 1K tokens (USD)"
            name="input_price_per_1k_tokens"
            type="number"
            value={formData.input_price_per_1k_tokens}
            onChange={handleFormChange}
            margin="normal"
            inputProps={{ step: '0.000001', min: '0' }}
          />

          <TextField
            fullWidth
            label="Output Price per 1K tokens (USD)"
            name="output_price_per_1k_tokens"
            type="number"
            value={formData.output_price_per_1k_tokens}
            onChange={handleFormChange}
            margin="normal"
            inputProps={{ step: '0.000001', min: '0' }}
          />

          <TextField
            fullWidth
            label="Minimum Cost per Call (USD)"
            name="minimum_cost_per_call"
            type="number"
            value={formData.minimum_cost_per_call}
            onChange={handleFormChange}
            margin="normal"
            inputProps={{ step: '0.000001', min: '0' }}
          />

          {editingModel && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Current version: {editingModel.version}. Updating will expire this version and create a new one.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSavePricing}
            variant="contained"
            disabled={loading}
          >
            {editingModel ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingManagementPage;
